import { createContext, useContext, useSyncExternalStore, type ReactElement, type ReactNode } from 'react';

import type { AuthStatus, SessionSnapshot } from '@/lib/auth/session-store.js';
import { userSessionStore, type UserDto } from '@/lib/auth/user-session-store.js';

/**
 * React-обёртка над модульным стором сессии.
 *
 * Источник истины — сам стор (lib/auth/session-store.ts), а не состояние React:
 * apiFetch читает access-токен синхронно и вне дерева, из хука это невозможно.
 * useSyncExternalStore подписывает дерево на тот же стор, поэтому двух источников
 * правды не возникает.
 */
interface AuthContextValue extends SessionSnapshot<UserDto> {
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const snapshot = useSyncExternalStore(
    userSessionStore.subscribe,
    userSessionStore.getSnapshot,
    userSessionStore.getSnapshot,
  );

  const value: AuthContextValue = {
    ...snapshot,
    isAuthenticated: snapshot.status === 'authenticated',
    isLoading: snapshot.status === 'loading',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === undefined) throw new Error('useAuth() called outside <AuthProvider>');
  return value;
}

export type { AuthStatus };
